import styled, { css } from 'styled-components';

import { Checkmark } from 'shared/icons/Checkmark';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { useWantedProductTemplates } from 'search/hooks/useWantedProductTemplates';
import { t } from 'localization/utils/t';

const TP = 'shared.components.GridCellProductInfo';

interface IGridCellProductInfo {
  id: string;
  isActiveCell: boolean;
  name: string;
  price?: number;
  productCondition: string;
  releaseDate?: number | string;
  retailPrice: number;
  slug: string;
}

export const GridCellProductInfo = ({
  id,
  isActiveCell,
  name,
  price,
  productCondition,
  releaseDate,
  retailPrice,
  slug,
}: IGridCellProductInfo) => {
  const { handleWantsModalOpen, wantedProductTemplates } = useWantedProductTemplates();
  const isWanted = wantedProductTemplates.has(id);
  const isUnderRetail = price < retailPrice && productCondition === SHOE_CONDITIONS.NEW_NO_DEFECTS;

  const handleWantClick = (e) => {
    e.preventDefault();
    handleWantsModalOpen(id, slug);
  };

  return (
    <GridCellProductInfo.ProductInfo>
      <GridCellProductInfo.YearNameWrapper>
        <GridCellProductInfo.Name data-qa="grid_cell_product_name">{name}</GridCellProductInfo.Name>
        <GridCellProductInfo.Year data-qa="grid_cell_product_release_date">
          {releaseDate}
        </GridCellProductInfo.Year>
      </GridCellProductInfo.YearNameWrapper>
      <GridCellProductInfo.PriceOrWant $isActiveCell={isActiveCell}>
        {isActiveCell ? (
          <GridCellProductInfo.Want
            data-qa="grid_cell_product_want"
            onClick={handleWantClick}
            type="button"
          >
            {t(`${TP}.want`, 'Want')}
            {isWanted && (
              <>
                &nbsp;
                <Checkmark />
              </>
            )}
          </GridCellProductInfo.Want>
        ) : (
          <GridCellProductInfo.PriceWrapper>
            {price && (
              <>
                <GridCellProductInfo.Price data-qa="grid_cell_product_price">
                  <LocalizedCurrency price={price} roundDownToDollarAmount />
                </GridCellProductInfo.Price>
                {isUnderRetail && (
                  <GridCellProductInfo.RetailPrice data-qa="grid_cell_retail_price">
                    <LocalizedCurrency price={retailPrice} roundDownToDollarAmount />
                  </GridCellProductInfo.RetailPrice>
                )}
              </>
            )}
          </GridCellProductInfo.PriceWrapper>
        )}
      </GridCellProductInfo.PriceOrWant>
    </GridCellProductInfo.ProductInfo>
  );
};

GridCellProductInfo.ProductInfo = styled.div`
  color: ${({ theme }) => theme.navigation.textColor};
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  flex: 1;
`;

GridCellProductInfo.YearNameWrapper = styled.div`
  color: ${({ theme }) => theme.navigation.textColor};
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: flex;
    flex-direction: row;
    gap: 16px;
  }
`;

GridCellProductInfo.Year = styled.div`
  color: ${({ theme }) => theme.navigation.textColor};
  height: 16px;
  min-width: 38px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    order: 1;
    max-width: 50px;
  }
`;

GridCellProductInfo.Name = styled.div`
  color: ${({ theme }) => theme.navigation.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-break: break-word;
  max-height: 48px; // line-height of 16px * line-clamp of 3

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    order: 2;
    max-height: 42px; // line-height of 14px * line-clamp of 3
  }
`;

GridCellProductInfo.PriceOrWant = styled.div<{ $isActiveCell?: boolean }>`
  color: ${({ theme }) => theme.navigation.textColor};
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  min-width: 50px;
  min-height: 32px;

  ${({ $isActiveCell }) =>
    $isActiveCell &&
    css`
      text-decoration: underline;
      text-underline-offset: 2px;
    `}
`;

GridCellProductInfo.Want = styled.button`
  align-items: center;
  display: flex;
  flex-direction: row;
  padding: 0;
  white-space: nowrap;
  color: ${({ theme }) => theme.navigation.textColor};
`;

GridCellProductInfo.Price = styled.div`
  color: ${({ theme }) => theme.navigation.textColor};
`;

GridCellProductInfo.RetailPrice = styled.div`
  color: ${({ theme }) => theme.navigation.textColor};
  text-decoration: line-through;
`;

GridCellProductInfo.PriceWrapper = styled.div`
  color: ${({ theme }) => theme.navigation.textColor};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
